<template>
  <div class="modal fade" id="preScheduleFormQuestionModal"
       tabindex="-1" aria-labelledby="preScheduleFormQuestionModal" aria-hidden="true">
    <div class="modal-dialog modal-xxl">
      <div class="modal-content">
        <div class="modal-header bg-light">
          <div class="d-flex align-items-center">
            <div>
              <h5 class="modal-title mb-0">Gerenciar Perguntas</h5>
              <span class="text-muted fs-7">Configure as perguntas do formulário de pré-agendamento</span>
            </div>
          </div>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>

        <div class="modal-body p-0">
          <!-- Seção de Adicionar Nova Pergunta -->
          <div class="card border-0 shadow-none">
            <div class="card-header bg-primary bg-opacity-10 border-bottom-0">
              <div class="card-title mb-0">
                <span class="fw-bold text-white">Adicionar Nova Pergunta</span>
              </div>
            </div>
            <div class="card-body">
              <div class="row g-4">
                <!-- Texto da Pergunta -->
                <div class="col-12">
                  <label class="form-label fw-semibold required">
                    Texto da Pergunta
                  </label>
                  <input
                    v-model="newQuestion.question_text"
                    class="form-control form-control-solid"
                    placeholder="Ex: Qual é a sua idade?"
                    required
                  />
                </div>

                <!-- Tipo, Ordem e Obrigatória -->
                <div class="col-md-4">
                  <label class="form-label fw-semibold required">
                    Tipo de Pergunta
                  </label>
                  <select v-model="newQuestion.question_type" class="form-select form-select-solid" required>
                    <option value="text">Texto Livre</option>
                    <option value="radio">Escolha Única</option>
                    <option value="checkbox">Múltipla Escolha</option>
                    <option value="file">Arquivo</option>
                  </select>
                </div>

                <div class="col-md-3">
                  <label class="form-label fw-semibold">
                    Ordem
                  </label>
                  <input
                    type="number"
                    v-model="newQuestion.question_order"
                    class="form-control form-control-solid"
                    min="0"
                    :placeholder="nextOrderNumber"
                    required
                  />
                </div>

                <div class="col-md-2">
                  <label class="form-label fw-semibold">
                    Obrigatória
                  </label>
                  <div class="form-check form-switch d-flex align-items-center mt-3">
                    <input class="form-check-input" type="checkbox" role="switch"
                           v-model="newQuestion.required" id="required-switch">
                    <label class="form-check-label ms-2 fw-normal" for="required-switch">
                      {{ newQuestion.required ? 'Sim' : 'Não' }}
                    </label>
                  </div>
                </div>

                <div class="col-md-3 d-flex align-items-end">
                  <button
                    type="button"
                    class="btn btn-primary w-100"
                    @click="submitQuestion"
                    :disabled="!canSubmitQuestion"
                  >
                    Salvar Pergunta
                  </button>
                </div>

                <!-- Opções de Resposta -->
                <div class="col-12" v-if="newQuestion.question_type === 'radio' || newQuestion.question_type === 'checkbox'">
                  <div class="separator separator-dashed my-4"></div>
                  <label class="form-label fw-semibold required">
                    Opções de Resposta
                  </label>

                  <div class="row g-3">
                    <div v-for="(option, index) in newQuestion.options" :key="index" class="col-12">
                      <div class="d-flex align-items-center gap-2">
                        <span class="badge badge-circle badge-light-primary fs-7 w-30px h-30px d-flex align-items-center justify-content-center">
                          {{ index + 1 }}
                        </span>
                        <input
                          type="text"
                          v-model="option.label"
                          class="form-control form-control-solid"
                          :placeholder="`Opção ${index + 1}`"
                          required
                        />
                        <input
                          type="text"
                          v-model="option.value"
                          class="form-control form-control-solid"
                          placeholder="Valor (opcional)"
                        />
                        <button
                          type="button"
                          class="btn btn-sm btn-icon btn-light-danger"
                          @click="removeOption(index)"
                          v-if="newQuestion.options.length > 1"
                          title="Remover opção"
                        >
                          <i class="bi bi-trash"></i>
                        </button>
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    class="btn btn-sm btn-outline-primary mt-3"
                    @click="addOption"
                  >
                    Adicionar Opção
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Seção de Perguntas Existentes -->
          <div class="separator separator-dashed"></div>

          <div class="px-6 py-4">
            <div class="d-flex align-items-center justify-content-between mb-4">
              <div>
                <h6 class="mb-0 fw-bold">Perguntas Cadastradas</h6>
                <span class="text-muted fs-7">{{ questions.length }} pergunta(s)</span>
              </div>
            </div>

            <!-- Lista de Perguntas -->
            <div v-if="questions.length > 0" class="row g-4">
              <div v-for="(question, index) in sortedQuestions" :key="question.id_pre_schedule_question || index"
                   class="col-12">
                <div class="card card-bordered">
                  <div class="card-body p-4">
                    <div class="row align-items-start">
                      <!-- Ordem -->
                      <div class="col-auto">
                        <div class="symbol symbol-35px">
                          <div class="symbol-label bg-light-primary text-primary fw-bold">
                            {{ question.question_order }}
                          </div>
                        </div>
                      </div>

                      <!-- Conteúdo da Pergunta -->
                      <div class="col">
                        <div class="d-flex flex-column flex-md-row justify-content-between gap-3">
                          <div class="flex-grow-1">
                            <h6 class="mb-2 text-gray-800">{{ question.question_text }}</h6>

                            <div class="d-flex flex-wrap gap-2 mb-2">
                              <!-- Tipo -->
                              <span class="badge" :class="getQuestionTypeBadgeClass(question.question_type)">
                                {{ getQuestionTypeLabel(question.question_type) }}
                              </span>

                              <!-- Obrigatória -->
                              <span v-if="question.required" class="badge badge-warning">
                                Obrigatória
                              </span>
                            </div>

                            <!-- Opções -->
                            <div v-if="question.options && question.options.length > 0" class="text-muted fs-7">
                              {{ question.options.map(o => o.label || o.value).join(' • ') }}
                            </div>
                          </div>

                          <!-- Ações -->
                          <div class="d-flex gap-2">
                            <button
                              type="button"
                              class="btn btn-sm btn-icon btn-light-danger"
                              @click="removeQuestion(question.id_pre_schedule_question)"
                              title="Remover pergunta"
                            >
                              <i class="bi bi-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Estado Vazio -->
            <div v-else class="text-center py-8">
              <div class="symbol symbol-50px mb-4">
                <div class="symbol-label bg-light">
                  <i class="bi bi-question-circle text-muted fs-2x"></i>
                </div>
              </div>
              <h6 class="text-muted mb-2">Nenhuma pergunta cadastrada</h6>
              <p class="text-muted fs-7">Adicione perguntas acima para começar a configurar seu formulário</p>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-light" @click="closeModal">
            Fechar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Modal from "@/helpers/modal";
import http from "@/helpers/http";
import Alert from "@/helpers/alert";

export default {
  name: "PreScheduleFormQuestionModal",
  props: ['form'],
  data() {
    return {
      formLocal: null,
      newQuestion: {
        question_text: '',
        question_type: 'text',
        question_order: 0,
        required: false,
        options: [],
      },
      questions: [],
    };
  },
  computed: {
    place() {
      return this.$store.state.auth.place;
    },
    sortedQuestions() {
      return [...this.questions].sort((a, b) => a.question_order - b.question_order);
    },
    canSubmitQuestion() {
      if (!this.newQuestion.question_text || !this.newQuestion.question_type) {
        return false;
      }

      if (this.newQuestion.question_type === 'radio' || this.newQuestion.question_type === 'checkbox') {
        if (!this.newQuestion.options || this.newQuestion.options.length === 0) {
          return false;
        }
        // Verificar se todas as opções têm label
        return this.newQuestion.options.every(opt => opt.label && opt.label.trim() !== '');
      }

      return true;
    }
  },
  watch: {
    form: {
      immediate: true,
      handler(newValue) {
        this.formLocal = newValue;
        if (newValue && newValue.questions) {
          this.questions = newValue.questions.map(q => ({
            ...q,
            options: q.options || []
          }));
        } else {
          this.questions = [];
        }
      }
    },
    'newQuestion.question_type'(newType) {
      if (newType === 'radio' || newType === 'checkbox') {
        if (!this.newQuestion.options || this.newQuestion.options.length === 0) {
          this.newQuestion.options = [{label: '', value: ''}];
        }
      } else {
        this.newQuestion.options = [];
      }
    }
  },
  mounted() {
    this.uniqueKey = this.date().unix().toString();
  },
  methods: {
    date: moment,
    getQuestionTypeLabel(type) {
      const labels = {
        'text': 'Texto',
        'radio': 'Escolha Única',
        'checkbox': 'Múltipla Escolha',
        'file': 'Arquivo',
      };
      return labels[type] || type;
    },
    getQuestionTypeBadgeClass(type) {
      const classes = {
        'text': 'badge-light-primary',
        'radio': 'badge-light-info',
        'checkbox': 'badge-light-success',
        'file': 'badge-light-warning',
      };
      return classes[type] || 'badge-light-secondary';
    },
    addOption() {
      if (!this.newQuestion.options) {
        this.newQuestion.options = [];
      }
      this.newQuestion.options.push({label: '', value: ''});
    },
    removeOption(index) {
      this.newQuestion.options.splice(index, 1);
    },
    async removeQuestion(questionId) {
      Swal.fire({
        title: 'Atenção',
        text: "Tem certeza que deseja remover esta pergunta?",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, remover!',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: "btn btn-danger",
          cancelButton: 'btn btn-secundary'
        }
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            // Remove da lista local
            this.questions = this.questions.filter(q => q.id_pre_schedule_question !== questionId);
            
            // Atualiza o formulário no backend removendo a pergunta
            const formData = {
              form_name: this.formLocal.form_name,
              form_description: this.formLocal.form_description,
              schedule_type: this.formLocal.schedule_type,
              id_spec_group: this.formLocal.id_spec_group,
              id_prof: this.formLocal.id_prof,
              id_paymeth: this.formLocal.id_paymeth,
              send_before_hours: this.formLocal.send_before_hours,
              send_via: this.formLocal.send_via,
              active: this.formLocal.active,
              questions: this.questions.map(q => ({
                question_text: q.question_text,
                question_type: q.question_type,
                question_order: q.question_order,
                required: q.required,
                options: q.options,
              }))
            };

            await http.put(`/places/${this.place.id_place}/pre-schedule-forms/${this.formLocal.id_pre_schedule_form}`, formData);
            
            Alert.toastSuccess('Pergunta removida com sucesso.');
            this.$emit('questionsUpdated');
          } catch (error) {
            Alert.toastError(error.response?.data?.message || 'Erro ao remover pergunta.');
          }
        }
      });
    },
    async submitQuestion() {
      if (!this.canSubmitQuestion) {
        Alert.toastWarning('Preencha todos os campos obrigatórios');
        return;
      }

      if (!this.formLocal || !this.formLocal.id_pre_schedule_form) {
        Alert.error('Formulário não encontrado');
        return;
      }

      try {
        const payload = {
          ...this.newQuestion,
          options: (this.newQuestion.question_type === 'radio' || this.newQuestion.question_type === 'checkbox')
            ? this.newQuestion.options.filter(opt => opt.label && opt.label.trim() !== '')
            : null,
        };

        // Atualizar o formulário com a nova pergunta
        const formData = {
          form_name: this.formLocal.form_name,
          form_description: this.formLocal.form_description,
          schedule_type: this.formLocal.schedule_type,
          id_spec_group: this.formLocal.id_spec_group,
          id_prof: this.formLocal.id_prof,
          id_paymeth: this.formLocal.id_paymeth,
          send_before_hours: this.formLocal.send_before_hours,
          send_via: this.formLocal.send_via,
          active: this.formLocal.active,
          questions: [
            ...this.questions.map(q => ({
              question_text: q.question_text,
              question_type: q.question_type,
              question_order: q.question_order,
              required: q.required,
              options: q.options,
            })),
            payload
          ]
        };

        await http.put(`/places/${this.place.id_place}/pre-schedule-forms/${this.formLocal.id_pre_schedule_form}`, formData);

        // Recarregar o formulário para pegar o ID da nova pergunta
        const response = await http.get(`/places/${this.place.id_place}/pre-schedule-forms/${this.formLocal.id_pre_schedule_form}`);
        this.questions = response.data.questions || [];

        Alert.toastSuccess('Pergunta adicionada com sucesso.');

        // Resetar formulário
        this.newQuestion = {
          question_text: '',
          question_type: 'text',
          question_order: this.questions.length > 0
            ? Math.max(...this.questions.map(q => q.question_order)) + 1
            : 0,
          required: false,
          options: [],
        };

        this.$emit('questionsUpdated');
      } catch (error) {
        Alert.toastError(error.response?.data?.message || 'Erro ao salvar pergunta.');
      }
    },
    closeModal() {
      Modal.toggle('#preScheduleFormQuestionModal');
      this.$emit('onClose');
    }
  },
}
</script>

<style scoped>
.modal-xxl {
  max-width: 1200px;
}

.separator {
  height: 1px;
  background-color: var(--bs-border-color);
  margin: 1rem 0;
}

.separator-dashed {
  background-image: linear-gradient(to right, var(--bs-border-color) 50%, transparent 50%);
  background-size: 8px 1px;
}

.badge-circle {
  border-radius: 50%;
}

.card-bordered {
  border: 1px solid var(--bs-border-color);
  border-radius: 0.75rem;
  box-shadow: 0 0.5rem 1.5rem 0.5rem rgba(0, 0, 0, 0.075);
}

.card-bordered:hover {
  box-shadow: 0 1rem 2rem 1rem rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
  transition: all 0.2s ease;
}

.symbol-35px {
  width: 35px;
  height: 35px;
}

.w-30px {
  width: 30px !important;
}

.h-30px {
  height: 30px !important;
}

.gap-2 {
  gap: 0.5rem;
}

.gap-3 {
  gap: 1rem;
}

.text-gray-800 {
  color: #3f4254 !important;
}

.fs-7 {
  font-size: 0.875rem;
}

.fs-2x {
  font-size: 2rem;
}

/* Responsividade */
@media (max-width: 768px) {
  .modal-xxl {
    margin: 0.5rem;
    max-width: calc(100vw - 1rem);
  }

  .card-body {
    padding: 1rem;
  }

  .d-flex.flex-column.flex-md-row {
    flex-direction: column;
    align-items: stretch !important;
  }

  .d-flex.gap-2 {
    justify-content: center;
  }
}

@media (max-width: 576px) {
  .col-md-4,
  .col-md-3,
  .col-md-2 {
    margin-bottom: 1rem;
  }

  .symbol-40px {
    width: 35px;
    height: 35px;
  }

  .symbol-40px .symbol-label {
    font-size: 0.875rem;
  }
}
</style>
